import { Dispatch } from "redux";
import { datadogLogs } from '@datadog/browser-logs';
import {
    FormattedIdentityProviderDetails,
    IdentityProviderDetails,
    IdentityProviderCategory,
    IdentityProviderClassification,
    IdentityProviderCompany,
    IdentityProviderLoa,
    IdentityProviderProtocol,
    IdentityProviderScheme,
    IdentityProviderChangeRequestResponse
} from "@idw-types/data/identity-providers";
import { setIdentityProviderDetails, setIdentityProviderDetailsLoading } from "@redux/actions/identity-provider-details-actions";
import { getIdentityProviderChangeRequest, getIdentityProviderDetailsAxnAdminRequest, getIdentityProviderDetailsAxnRequest } from "@requests/identity-provider";
import { formatIdentityProviderDetails } from "@formatters/identity-providers";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";
import { resources } from "@constants/hardcoded-strings";

const getIdentityProviderDetailsFromAxn = async (
    identityProviderId: string,
    dispatch: Dispatch,
    serviceNotFoundCallback: () => void,
    showError: boolean
): Promise<IdentityProviderDetails> => {
    const axnIdentityProviderDetailsResponse = await getIdentityProviderDetailsAxnRequest(identityProviderId);

    if (axnIdentityProviderDetailsResponse.data) {
        return axnIdentityProviderDetailsResponse.data;
    } else if (axnIdentityProviderDetailsResponse.error) {
        const errorCode = JSON.parse(axnIdentityProviderDetailsResponse.error).code;
        if (!showError && errorCode === 404) {
            serviceNotFoundCallback();
        } else {
            logAndDisplayErrorModal(
                dispatch,
                axnIdentityProviderDetailsResponse.error,
                resources.requests.getIdentityProviderDetailsAxn.error.title,
                resources.requests.getIdentityProviderDetailsAxn.error.message
            );
        }
    }
    return null;
};

export const getIdentityProviderDetailsReduxRequest = (dispatch: Dispatch) => async (
    identityProviderId: string,
    categories: Array<IdentityProviderCategory>,
    classifications: Array<IdentityProviderClassification>,
    companies: Array<IdentityProviderCompany>,
    loas: Array<IdentityProviderLoa>,
    protocols: Array<IdentityProviderProtocol>,
    schemes: Array<IdentityProviderScheme>,
    serviceNotFoundCallback: () => void,
    showError: boolean
): Promise<{
    formattedIdentityProviderDetails: FormattedIdentityProviderDetails;
    identityProviderChangeRequests: Array<IdentityProviderChangeRequestResponse>;
}> => {
    dispatch(setIdentityProviderDetailsLoading(true));
    const identityProviderChangeRequests = await getIdentityProviderChangeRequest(identityProviderId);

    let identityProviderChangeRequestsArray: Array<IdentityProviderChangeRequestResponse> = [];
    let identityProviderDetails: IdentityProviderDetails;

    if (identityProviderChangeRequests.data?.identityProviderChangeRequests) {
        // check change requests - if one exists, attempt to get data from admin
        if (identityProviderChangeRequests.data.identityProviderChangeRequests.length > 0) {
            identityProviderChangeRequestsArray = identityProviderChangeRequests.data.identityProviderChangeRequests;
            const axnAdminIdentityProviderDetailsResponse = await getIdentityProviderDetailsAxnAdminRequest(identityProviderId);
            // if admin data does not exist, attempt to get data from axn
            if (axnAdminIdentityProviderDetailsResponse.data) {
                identityProviderDetails = axnAdminIdentityProviderDetailsResponse.data;
            } else {
                const errorMessage = `Identity Provider with ID ${identityProviderId} has a change request but is not present in AXN Admin`;
                console.error(errorMessage);
                datadogLogs.logger.error(errorMessage);

                identityProviderDetails = await getIdentityProviderDetailsFromAxn(identityProviderId, dispatch, serviceNotFoundCallback, showError);
            }
        } else {
            // if no change requests exist, attempt to get data from axn
            identityProviderDetails = await getIdentityProviderDetailsFromAxn(identityProviderId, dispatch, serviceNotFoundCallback, showError);
        }
    }

    if (identityProviderDetails) {
        const formattedIdentityProviderDetails = formatIdentityProviderDetails(
            identityProviderDetails,
            categories,
            classifications,
            companies,
            loas,
            protocols,
            schemes
        );
        dispatch(setIdentityProviderDetails(formattedIdentityProviderDetails));
        return {
            formattedIdentityProviderDetails,
            identityProviderChangeRequests: identityProviderChangeRequestsArray
        };
    } else {
        dispatch(setIdentityProviderDetailsLoading(false));
        return null;
    }
};